import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { ControlContainer, NgForm, Validator } from '@angular/forms';
import { ObjectIdentifier } from '@emma-helpers/object-identifier.class';

import { EMMAFormElementComponent } from '../emma-form-element.component';
import {
  EMMAFormManyFooterTplDirective,
  EMMAFormManyHeaderTplDirective,
  EMMAFormManyItemTplDirective,
  EMMAFormManySuffixTplDirective,
} from './emma-form-many.directive';
import { isObject } from 'lodash';

/**
 * @description This component is used to create a list of items injectable into a form.
 * It tracks efficiently each element of the list using ObjectIdentifier.
 */
@Component({
    selector: 'emma-form-many',
    templateUrl: './emma-form-many.component.html',
    providers: [
        {
            provide: EMMAFormElementComponent,
            useExisting: EMMAFormManyComponent,
        },
        { provide: ControlContainer, useExisting: NgForm },
    ],
    standalone: false
})
export class EMMAFormManyComponent extends EMMAFormElementComponent implements Validator, OnChanges {
  // Custom templates
  /** Template de acciones a la derecha de la cabecera */
  @ContentChild(EMMAFormManyItemTplDirective, { read: TemplateRef, static: false })
  itemTemplate!: TemplateRef<unknown>;
  @ContentChild(EMMAFormManyHeaderTplDirective, { read: TemplateRef, static: false })
  headerTemplate!: TemplateRef<unknown>;
  @ContentChild(EMMAFormManyFooterTplDirective, { read: TemplateRef, static: false })
  footerTemplate!: TemplateRef<unknown>;
  @ContentChild(EMMAFormManySuffixTplDirective, { read: TemplateRef, static: false })
  suffixTemplate!: TemplateRef<unknown>;
  @Output() $add = new EventEmitter<number | undefined>();
  @Output() $remove = new EventEmitter<number>();
  // Form
  @Input() maxlength = Infinity;
  @Input() minlength = 0;

  @Input() trackByProp?: string;
  @Input() trackByValue?: boolean;
  @Input() trackBy?: (index: number, value: unknown) => unknown;

  @Input() shouldShowHeader = true;
  @Input() shouldShowFooter = true;
  @Input() shouldShowSuffix = true;

  // Strings for default templates
  @Input() addButtonLabel = $localize`Añadir`;
  @Input() removeButtonLabel = $localize`Eliminar`;

  @Input()
  get values(): Array<unknown> {
    return this.innerValues;
  }
  set values(values: Array<unknown>) {
    if (values !== this.innerValues || values.length !== this.innerValues.length) {
      this.innerValues = values;
    }
  }
  private innerValues: Array<unknown> = [];

  constructor(private objectIdentifier: ObjectIdentifier) {
    super();
  }

  override ngOnChanges(changes: SimpleChanges): void {
    if ('trackByProp' in changes || 'trackByValue' in changes || 'trackBy' in changes) {
      if (this.trackBy && (this.trackByValue || this.trackByProp)) {
        throw new Error('You can not use trackByProp or trackByValue with trackBy');
      } else if (this.trackByProp && this.trackByValue) {
        throw new Error('You can not use trackByProp and trackByValue at the same time');
      }
    }
  }

  onBlur() {
    this.$blur.emit();
  }

  onAdd(index?: number) {
    this.$add.emit(index);
  }

  onRemove(index: number) {
    this.$remove.emit(index);
  }

  trackItems = (index: number, value: unknown) => {
    if (this.trackBy) {
      return this.trackBy(index, value);
    } else if (isObject(value) && this.trackByProp) {
      return (value as { [key: string]: unknown })?.[this.trackByProp];
    } else if (this.trackByValue) {
      return value;
    } else {
      return this.objectIdentifier.id(value);
    }
  };
}
